<script>
import AkSidebarV2 from '@components/v2/general/AkSidebarV2.vue';
import ThreadTypeCard from '@views/threads/parts/ThreadTypeCard.vue';
import AkLoader from '@components/general/AkLoader.vue';
import threadService from '@services/threadService';
import messageMixin from '@mixins/messageMixin';
import threadMixin from '@mixins/const/threadMixin';
import UtilsMixin from '@mixins/utilsMixin';

export default {
  name: 'OpportuniteDetailSidebar',
  components: {AkLoader, ThreadTypeCard, AkSidebarV2},
  mixins: [messageMixin, threadMixin, UtilsMixin],
  props: {
    showStatusBanner: {
      type: Boolean,
      default: false,
    },
    showStaffLinks: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update'],
  data() {
    return {
      thread: null,
    };
  },
  computed: {
    threadType() {
      return this.thread?.typeDetails?.type;
    },
    queryMessage() {
      return this.thread.conversation?.messages[0];
    },
  },
  methods: {
    open(threadId) {
      this.thread = null;
      this.$refs.sidebar.show();
      this.$refs.loader.load();
      threadService
        .getDetails(threadId)
        .then((data) => {
          this.thread = data;
        })
        .catch((e) => {
          this.addError('Erreur lors du chargement des détails : ' + this.msgForErr(this.$t, e));
          this.$refs.sidebar.close();
        })
        .finally(() => {
          this.$refs.loader.hide();
        });
    },
    handleUpdate(e) {
      this.$emit('update', e);
      this.thread.typeDetails = e;
    },
  },
};
</script>

<template>
  <AkSidebarV2 ref="sidebar" title="Opportunité" @close="() => $emit('close')" dismissable>
    <AkLoader ref="loader">
      <template #content>
        <div
          v-if="showStatusBanner && (isReservationExpired || isReservationRetracted)"
          class="bg-red-50 text-center text-red text-sm font-medium p-3">
          {{
            isReservationExpired
              ? 'Cette demande a été refusée automatiquement par Airbnb'
              : 'Cette demande a été retirée par le voyageur'
          }}
        </div>
        <div v-if="thread" class="p-4 flex flex-col gap-4 overflow-y-auto">
          <router-link
            v-if="showStaffLinks"
            target="_blank"
            :to="{name: 'accommodationPage', params: {accommodationId: thread.accommodation.id}}"
            class="p-2 pl-3 flex gap-2 justify-between bg-secondary-50 rounded-md items-center focus-visible:bg-secondary-100 hover:bg-secondary-100">
            <span class="text-primary font-medium truncate">{{ thread.accommodation.name }}</span>
            <i class="ga-icon ga-long_arrow_up text-xl text-primary rotate-45 shrink-0" />
          </router-link>
          <span v-else class="p-2 pl-3 bg-secondary-50 rounded-md text-primary font-medium truncate shrink-0">
            {{ thread.accommodation.name }}
          </span>
          <div class="flex gap-2.5">
            <span
              v-if="!queryMessage.authorPictureUrl"
              class="flex items-center justify-center font-semibold h-8 w-8 rounded-full bg-grey-50 text-grey-500 text-sm">
              {{ queryMessage.authorDisplay[0] }}
            </span>
            <img
              v-else
              :src="queryMessage.authorPictureUrl"
              :alt="queryMessage.authorDisplay[0]"
              class="w-8 h-8 rounded-full" />

            <div class="flex flex-col">
              <router-link
                v-if="showStaffLinks"
                :to="{name: 'clientPage', params: {clientId: thread.guest.id}}"
                class="text-md font-semibold text-grey-900 flex items-center gap-1.5">
                {{ queryMessage.authorDisplay }}
                <i class="ga-icon ga-long_arrow_up text-md rotate-45 inline-block mb-0.5" />
              </router-link>
              <span v-else class="text-md font-semibold text-grey-900">{{ queryMessage.authorDisplay }}</span>

              <div
                class="rounded-lg ml-2.5 rounded-tl-none p-2.5 bg-grey-50 text-grey-900 font-medium whitespace-pre-wrap w-fit relative messageBubble mt-1">
                {{ queryMessage.translatedContent ?? queryMessage.originalContent }}
              </div>

              <router-link
                v-if="showStaffLinks"
                :to="{name: 'threadDetails', params: {threadId: thread.id}}"
                class="text-[11px] text-grey-500 hover:underline ml-2.5">
                Voir la conversation
              </router-link>
            </div>
          </div>

          <ThreadTypeCard v-model="thread" @update="handleUpdate" />
        </div>
      </template>
    </AkLoader>
  </AkSidebarV2>
</template>

<style scoped>
.messageBubble::before {
  content: '';
  border-bottom: 10px solid;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  rotate: 180deg;
  @apply top-0 -left-2.5 absolute border-b-grey-50;
}
</style>
